import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon } from 'lucide-react';
import { Eyebrow } from '../components/ui/Eyebrow';
import { programs } from '../data/programs';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
const goals = ['Energy, mood and sleep', 'Body composition or weight', 'Sexual health', 'Longevity and prevention'];
export function GetStarted() {
  const [step, setStep] = useState(0);
  const [program, setProgram] = useState<string | null>(null);
  const [goal, setGoal] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  useDocumentMeta({
    title: 'Start your intake — Republic',
    description: 'Three questions to start. Nothing is charged until a clinician reviews your labs and you approve the protocol.'
  });
  const total = 3;
  const canAdvance = step === 0 ? Boolean(program) : step === 1 ? Boolean(goal) : email.includes('@');
  if (submitted) {
    return <main className="bg-cream" data-id="element-311" data-mp="d44kcapb6wmu">
        <div className="mx-auto max-w-shell px-6 py-28 md:px-8 md:py-36" data-id="element-312" data-mp="d2thqe7lnuxb">
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.28,
          ease: [0.23, 1, 0.32, 1]
        }} className="max-w-2xl" data-id="element-313" data-mp="r3mdf29n0w20">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brick text-cream-light" data-id="element-314" data-mp="3ukuqz9xc19qx">
              <CheckIcon className="h-5 w-5" strokeWidth={2} data-id="element-315" data-mp="3pix322dzlqrn" />
            </span>
            <h1 className="mt-8 font-serif text-4xl leading-tight text-ink md:text-6xl" data-id="element-316" data-mp="8632ft3md3cy">
              Your intake is saved.
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-ink-soft" data-id="element-317" data-mp="3btlin3zasr0x">
              A clinician licensed in your state will review it and send your lab requisition to{' '}
              <span className="text-ink" data-id="element-318" data-mp="36sebn74zbvvb">{email}</span> within one business day. Nothing is charged
              until you approve a protocol.
            </p>
            <Link to="/programs" className="mt-10 inline-flex items-center gap-2 border-b border-brick pb-1 text-sm text-brick transition-colors duration-150 ease-republic hover:border-ink hover:text-ink" data-id="element-319" data-mp="1fpf4unpq21tv">
              Keep reading about the programs
              <ArrowRightIcon className="h-4 w-4" data-id="element-320" data-mp="rt9r2dd5o604" />
            </Link>
          </motion.div>
        </div>
      </main>;
  }
  return <main className="bg-cream" data-id="element-321" data-mp="wrusgg458vq5">
      <div className="mx-auto max-w-shell px-6 py-16 md:px-8 md:py-24" data-id="element-322" data-mp="10t693w51i6m7">
        <div className="grid gap-14 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20" data-id="element-323" data-mp="10vamd4otwhz5">
          <div data-id="element-324" data-mp="15t2fu354gc5g">
            <Eyebrow className="text-brick" data-id="element-325" data-mp="15tkipimahomc">Intake</Eyebrow>
            <h1 className="mt-4 font-serif text-4xl leading-[1.04] text-ink md:text-[3.5rem]" data-id="element-326" data-mp="2c94lo9zffas6">
              Three questions to start.
            </h1>
            <p className="mt-6 max-w-md text-base leading-relaxed text-ink-soft" data-id="element-327" data-mp="2h995a8vmslmm">
              This takes about a minute. The full medical history comes after a clinician confirms
              Republic is a fit for what you are treating.
            </p>
            <p className="rule mt-10 pt-5 text-sm text-ink-muted" data-id="element-328" data-mp="2hki76t0b1eeq">
              Step {step + 1} of {total}
            </p>
          </div>

          <div data-id="element-329" data-mp="2dhvi2zv0q7l8">
            {step === 0 && <Field label="Which program are you interested in?" data-id="element-330" data-mp="28l54cj6oro2z">
                <div className="grid gap-3 sm:grid-cols-2" data-id="element-331" data-mp="23ls8xa1r18os">
                  {programs.map(item => <Choice key={item.slug} selected={program === item.slug} onClick={() => setProgram(item.slug)} data-id="element-332" data-mp="1ymfkwlz55vkm">
                      {item.name}
                    </Choice>)}
                </div>
              </Field>}

            {step === 1 && <Field label="What matters most to you right now?" data-id="element-333" data-mp="3ox0skq1e5afe">
                <div className="grid gap-3 sm:grid-cols-2" data-id="element-334" data-mp="3w3moweahmtnl">
                  {goals.map(item => <Choice key={item} selected={goal === item} onClick={() => setGoal(item)} data-id="element-335" data-mp="4vi9shsjc6e8">
                      {item}
                    </Choice>)}
                </div>
              </Field>}

            {step === 2 && <Field label="Where should we send your lab requisition?" data-id="element-336" data-mp="3dbbrv12ilpxe">
                <label htmlFor="email" className="sr-only" data-id="element-337" data-mp="3da0miwtf8a46">
                  Email address
                </label>
                <input id="email" type="email" value={email} onChange={event => setEmail(event.target.value)} placeholder="you@email.com" className="w-full border border-ink/25 bg-cream-light px-5 py-4 text-base text-ink placeholder:text-ink-muted focus:border-ink" data-id="element-338" data-mp="33dxoo35lqxn1" />
                <p className="mt-4 text-sm leading-relaxed text-ink-muted" data-id="element-339" data-mp="2fq91t5lecev4">
                  Republic never sells patient data. Records are stored in a HIPAA-compliant system
                  and downloadable by you at any time.
                </p>
              </Field>}

            <div className="rule mt-10 flex items-center justify-between pt-6" data-id="element-340" data-mp="2oom8nc6heuma">
              <button type="button" onClick={() => setStep(value => Math.max(0, value - 1))} disabled={step === 0} className="inline-flex items-center gap-2 text-sm text-ink-muted transition-colors duration-150 ease-republic hover:text-ink disabled:opacity-40" data-id="element-341" data-mp="2ko2d2d1k3z29">
                <ArrowLeftIcon className="h-4 w-4" data-id="element-342" data-mp="33fbleizlchs9" />
                Back
              </button>
              <button type="button" disabled={!canAdvance} onClick={() => step === total - 1 ? setSubmitted(true) : setStep(step + 1)} className="inline-flex items-center gap-2 bg-brick px-7 py-3.5 text-sm font-medium text-cream-light transition-colors duration-150 ease-republic hover:bg-brick-deep disabled:cursor-not-allowed disabled:bg-stone disabled:text-cream-light" data-id="element-343" data-mp="3hf35n1gpauep">
                {step === total - 1 ? 'Submit intake' : 'Continue'}
                <ArrowRightIcon className="h-4 w-4" data-id="element-344" data-mp="2nvjxdk21sagu" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>;
}
function Field({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-345" data-mp="2xuiy8ggkn1ta">
      <h2 className="font-serif text-2xl leading-snug text-ink md:text-3xl" data-id="element-346" data-mp="32rsvgia7chro">{label}</h2>
      <div className="mt-6" data-id="element-347" data-mp="32py9d0n72jm0">{children}</div>
    </div>;
}
function Choice({
  selected,
  onClick,
  children
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return <button type="button" onClick={onClick} aria-pressed={selected} className={`flex items-center justify-between gap-3 px-5 py-4 text-left text-sm transition-colors duration-150 ease-republic ${selected ? 'bg-ink text-cream-light' : 'border border-ink/20 text-ink-soft hover:border-ink hover:text-ink'}`} data-id="element-348" data-mp="18g4ekakeu54n">
      {children}
      {selected && <CheckIcon className="h-4 w-4 shrink-0" strokeWidth={2} data-id="element-349" data-mp="r4fm1govfbt4" />}
    </button>;
}